<template>
  <v-dialog
    :value="value"
    max-width="780"
    persistent
    @input="onClose"
  >
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-h6">Link statement row to bill</span>
        <v-btn
          icon
          :disabled="submitting"
          @click="onClose(false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert
          type="info"
          outlined
          dense
          class="mb-3"
        >
          <div class="text-body-2">
            Linking <strong>{{ contextLine }}</strong>
          </div>
        </v-alert>

        <v-text-field
          v-model="query"
          label="Search customer name, COM_/CUS_ id, phone, or bill number"
          prepend-inner-icon="mdi-magnify"
          dense
          outlined
          clearable
          hide-details
          autofocus
          @input="onQueryChange"
        />

        <div class="mt-3">
          <v-data-table
            :headers="headers"
            :items="candidates"
            :loading="searching"
            :no-data-text="searching ? 'Searching…' : (query ? 'No candidates found.' : 'Type to search.')"
            hide-default-footer
            disable-pagination
            disable-sort
            item-key="id"
            :item-class="rowClass"
            @click:row="onSelect"
          >
            <template #item.customer="{ item }">
              <div>{{ item.customer && item.customer.name ? item.customer.name : '—' }}</div>
              <div class="text-caption grey--text">
                {{ item.customer && item.customer.id ? item.customer.id : '' }}
              </div>
            </template>
            <template #item.period="{ item }">
              {{ item.period }} · {{ formatAmount(item.total) }}
            </template>
            <template #item.status="{ item }">
              <v-chip
                x-small
                :color="statusColor(item.status)"
                text-color="white"
              >
                {{ item.status }}
              </v-chip>
            </template>
            <template #item.delta="{ item }">
              <span :class="deltaClass(item)">{{ formatDelta(item) }}</span>
            </template>
          </v-data-table>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          text
          :disabled="submitting"
          @click="onClose(false)"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="submitting"
          :disabled="!selected"
          @click="onConfirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { $axios } from '@/store/apis';
import { linkBill } from '@/store/apis/bulkReconciliation';

const STATUS_COLORS = {
  approved: 'success',
  paid: 'success',
  partial: 'amber darken-2',
  unpaid: 'orange',
  cancelled: 'grey',
};

export default {
  name: 'BulkLinkBillDialog',
  props: {
    value: { type: Boolean, default: false },
    uuid: { type: String, required: true },
    record: { type: Object, default: null },
  },
  data() {
    return {
      query: '',
      candidates: [],
      selected: null,
      searching: false,
      submitting: false,
      searchDebounce: null,
      headers: [
        { text: 'Bill #', value: 'number' },
        { text: 'Customer', value: 'customer' },
        { text: 'Period · total', value: 'period' },
        { text: 'Status', value: 'status' },
        { text: 'Δ', value: 'delta' },
      ],
    };
  },
  computed: {
    contextLine() {
      const r = this.record;
      if (!r) return '';
      const parts = [
        r.bank,
        r.statement_date,
        `${this.formatAmount(r.amount)} LAK`,
        r.transaction_ref ? `ref ${r.transaction_ref}` : null,
        r.customer_name ? `hint ${r.customer_name}` : null,
      ].filter(Boolean);
      return parts.join(' · ');
    },
    confirmLabel() {
      if (!this.selected) return 'Link selected';
      return `Link selected (${this.selected.number || `BILL-${this.selected.id}`})`;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.query = '';
        this.candidates = [];
        this.selected = null;
      }
    },
  },
  beforeDestroy() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
  methods: {
    onClose(open) {
      this.$emit('input', !!open && open !== false ? open : false);
    },
    onQueryChange() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.search(), 300);
    },
    async search() {
      const q = (this.query || '').trim();
      if (!q) {
        this.candidates = [];
        return;
      }
      this.searching = true;
      try {
        const res = await $axios.get('bills/search', { params: { q, limit: 10 } });
        this.candidates = res.data?.data || res.data || [];
        this.selected = null;
      } catch (err) {
        const msg = err.response?.data?.message || 'Bill search failed.';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.searching = false;
      }
    },
    onSelect(row) {
      this.selected = row;
    },
    rowClass(item) {
      return this.selected && this.selected.id === item.id ? 'blue lighten-5' : '';
    },
    formatAmount(n) {
      if (n === null || n === undefined) return '—';
      return Number(n).toLocaleString();
    },
    formatDelta(item) {
      if (!this.record || !item.total) return '—';
      const delta = Number(this.record.amount) - Number(item.total);
      const sign = delta > 0 ? '+' : '';
      return `${sign}${delta.toLocaleString()}`;
    },
    deltaClass(item) {
      if (!this.record || !item.total) return 'grey--text';
      const delta = Number(this.record.amount) - Number(item.total);
      if (delta === 0) return 'success--text font-weight-bold';
      return 'orange--text text--darken-2 font-weight-bold';
    },
    statusColor(s) { return STATUS_COLORS[s] || 'grey'; },
    async onConfirm() {
      if (!this.selected || this.submitting) return;
      this.submitting = true;
      try {
        await linkBill(this.uuid, this.record.id, this.selected.id);
        this.$emit('linked');
        this.$emit('input', false);
      } catch (err) {
        const status = err.response?.status;
        const msg = err.response?.data?.message || 'Failed to link bill.';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
        if (status === 409) {
          // Backend already in conflicting state (record matched / batch closed / bill terminal).
          // Refresh the parent so the row re-renders correctly, and close.
          this.$emit('linked');
          this.$emit('input', false);
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
